/**
 * SaveBlockAsExampleDialog
 *
 * ⋮ → "Save as library blueprint": captures a semantic blueprint plus exact full
 * preview screens and writes them to the project block library. Shows what travels (capture
 * summary) per the approved mock (.design/block-examples-library).
 *
 * Pure UI: dual capture and persistence are injected (`capture` / `save`)
 * — BlockCanvasEditor wires StrategyExportService + BlockLibraryService.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, Stack, TextField, Typography } from '@mui/material';
// Subpath imports: the ui-components barrel drags WithGuideHelp/GuideService
// into jest where it cannot resolve (documented barrel trap).
import { BaseDialog } from '@modules/ui-components/dialogs/BaseDialog';
import { DialogHeader } from '@modules/ui-components/dialogs/DialogHeader';
import { DialogContent } from '@modules/ui-components/dialogs/DialogContent';
import { DialogFooter } from '@modules/ui-components/dialogs/DialogFooter';
import {
  BLOCK_LIBRARY_LIMITS,
  validateBlockLibraryExampleWrite,
} from '@modules/shared-types';
import type { BlockLibraryExampleInput } from '@modules/shared-types';
import type { BlockLibraryCapture } from '../services/StrategyExportService';
import { warningToneBg, warningToneText } from '../utils/warningTone';
import { STRATEGY_CANVAS } from '@test-kit/selectors/strategy-canvas';

export interface SaveBlockAsExampleDialogProps {
  open: boolean;
  /** Block display name — the name field prefill until the capture arrives. */
  blockName: string;
  /** Loads the v2 blueprint + full preview + capture summary. */
  capture: () => Promise<BlockLibraryCapture>;
  /** Persists the validated example (BlockLibraryService.create wiring). */
  save: (input: BlockLibraryExampleInput) => Promise<void>;
  onClose: () => void;
  /** Fired after a successful save (toast / palette refresh). */
  onSaved?: () => void;
}

export const SaveBlockAsExampleDialog: React.FC<SaveBlockAsExampleDialogProps> = ({
  open,
  blockName,
  capture,
  save,
  onClose,
  onSaved,
}) => {
  const [captureResult, setCaptureResult] = useState<BlockLibraryCapture | null>(null);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [name, setName] = useState(blockName);
  const [description, setDescription] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCaptureResult(null);
    setCaptureError(null);
    setSaveError(null);
    setName(blockName);
    setDescription('');
    setTagsText('');
    capture().then((result) => {
      if (cancelled) return;
      setCaptureResult(result);
      setName(result.defaults.name);
    }).catch((error: Error) => {
      if (cancelled) return;
      setCaptureError(error.message);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const overSize = Boolean(
    captureResult && captureResult.summary.payloadBytes > BLOCK_LIBRARY_LIMITS.maxPayloadBytes,
  );

  const tags = useMemo(
    () => tagsText.split(',').map((tag) => tag.trim()).filter(Boolean),
    [tagsText],
  );

  const handleSave = useCallback(async () => {
    if (!captureResult || overSize || saving) return;
    const input: BlockLibraryExampleInput = {
      ...captureResult.defaults,
      name: name.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(tags.length > 0 ? { tags } : {}),
      blueprint: captureResult.blueprint,
      previewScreens: captureResult.previewScreens,
      sourceChecksum: captureResult.sourceChecksum,
      enabled: true,
    };
    const validated = validateBlockLibraryExampleWrite(input);
    if (!validated.ok) {
      setSaveError(validated.errors.join(' '));
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await save(validated.value);
      onSaved?.();
      onClose();
    } catch (error) {
      setSaveError((error as Error).message);
    } finally {
      setSaving(false);
    }
  }, [captureResult, overSize, saving, name, description, tags, save, onSaved, onClose]);

  const handleClose = useCallback(() => {
    if (saving) return;
    onClose();
  }, [saving, onClose]);

  const summary = captureResult?.summary;

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      mode="modal"
      data-testid={STRATEGY_CANVAS.saveAsExampleDialog}
      slotProps={{ paper: { 'aria-label': 'Save as library blueprint' } }}
    >
      <DialogHeader title="Save as library blueprint" onClose={handleClose} />

      <DialogContent>
        <Stack spacing={2.5}>
          {/* Capture summary — show what travels (FP-7 honest states) */}
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
            }}
          >
            <Typography variant="overline" color="text.secondary">
              What will be captured
            </Typography>
            {!summary && !captureError && (
              <Typography variant="body2" color="text.secondary">
                Preparing the adaptation blueprint and full preview…
              </Typography>
            )}
            {captureError && (
              <Alert severity="error">{captureError}</Alert>
            )}
            {summary && (
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 0.5 }}
                data-testid={STRATEGY_CANVAS.saveAsExampleSummary}
              >
                <Chip size="small" label={`${summary.screens} simplified screens`} />
                <Chip size="small" label={`${summary.manualScreens} extracted manual`} />
                <Chip size="small" label={`${summary.contractInputs} inputs`} />
                <Chip
                  size="small"
                  label={`${summary.contractOutputs} output${summary.contractOutputs === 1 ? '' : 's'}`}
                />
                <Chip size="small" label={`${summary.instructions} instructions`} />
                <Chip size="small" label={`${summary.variables.length} variables`} />
                <Chip size="small" label={`${summary.evidenceReferences} evidence refs`} />
                <Chip size="small" label={`${summary.assetIntents} asset intents`} />
                <Chip size="small" label={`${summary.assetExamples} asset examples`} />
                {summary.hasAssetPlans && (
                  <Chip
                    size="small"
                    label="assets rebuild on insert"
                    sx={{
                      // AA pair — filled warning chip is white on #ED6C02 (3.11:1).
                      color: warningToneText,
                      bgcolor: warningToneBg,
                      border: 1,
                      borderColor: 'warning.main',
                    }}
                  />
                )}
              </Stack>
            )}
          </Box>

          {overSize && summary && (
            <Alert severity="error" data-testid={STRATEGY_CANVAS.saveAsExampleOversize}>
              This block serializes to {summary.payloadBytes.toLocaleString()} bytes — the library
              limit is {BLOCK_LIBRARY_LIMITS.maxPayloadBytes.toLocaleString()} bytes. Reduce the
              preview/editor payload before saving; nothing is truncated silently.
            </Alert>
          )}

          <TextField
            label="Example name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            fullWidth
            inputProps={{ 'data-testid': STRATEGY_CANVAS.saveAsExampleName, maxLength: BLOCK_LIBRARY_LIMITS.maxNameLength }}
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            fullWidth
            multiline
            minRows={2}
            inputProps={{ 'data-testid': STRATEGY_CANVAS.saveAsExampleDescription, maxLength: BLOCK_LIBRARY_LIMITS.maxDescriptionLength }}
          />
          <TextField
            label="Tags (comma-separated, optional)"
            value={tagsText}
            onChange={(event) => setTagsText(event.target.value)}
            fullWidth
            inputProps={{ 'data-testid': STRATEGY_CANVAS.saveAsExampleTags }}
            helperText={`Up to ${BLOCK_LIBRARY_LIMITS.maxTags} tags`}
          />

          {saveError && <Alert severity="error">{saveError}</Alert>}

          <Typography variant="caption" color="text.secondary">
            Full screens remain available for exact preview. Adaptation uses only the simplified
            blueprint; generated assets and provider settings are rebuilt for the target strategy.
          </Typography>
        </Stack>
      </DialogContent>

      <DialogFooter
        primaryAction={{
          label: saving ? 'Saving…' : 'Save blueprint',
          onClick: handleSave,
          disabled: !captureResult || overSize || !name.trim() || saving,
          loading: saving,
          'data-testid': STRATEGY_CANVAS.saveAsExampleSubmit,
        } as never}
      />
    </BaseDialog>
  );
};

SaveBlockAsExampleDialog.displayName = 'SaveBlockAsExampleDialog';
